import { supabase } from '../lib/supabase';

export interface PayPalOrderRequest {
  vendorEmail: string;
  vendorAmount: number;
  platformEmail: string;
  platformAmount: number;
  currency?: string;
}

export interface PayPalOrderResponse {
  success: boolean;
  orderId?: string;
  approvalUrl?: string;
  payerId?: string;
  payerEmail?: string;
  error?: string;
}

export interface PayPalStatusResponse {
  success: boolean;
  completed?: boolean;
  cancelled?: boolean;
  orderId?: string;
  payerId?: string;
  payerEmail?: string;
  error?: string;
}

export class PayPalService {
  /**
   * Create a PayPal payment order
   */
  static async createPaymentOrder(
    request: PayPalOrderRequest
  ): Promise<PayPalOrderResponse> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return {
          success: false,
          error: 'User not authenticated',
        };
      }

      // Call the edge function to create PayPal order
      const { data, error } = await supabase.functions.invoke('paypal-create-order', {
        body: {
          vendorEmail: request.vendorEmail,
          vendorAmount: request.vendorAmount,
          platformEmail: request.platformEmail,
          platformAmount: request.platformAmount,
          currency: request.currency || 'USD',
        },
      });

      if (error) {
        console.error('PayPal edge function error:', error);
        return {
          success: false,
          error: error.message || 'Failed to create PayPal order',
        };
      }

      if (!data || !data.success) {
        const missing = Array.isArray((data as any)?.missingFields) ? (data as any).missingFields.join(', ') : '';
        const userMsg = (data as any)?.userMessage ? String((data as any).userMessage) : '';
        return {
          success: false,
          error: userMsg || (missing ? `Missing required fields: ${missing}` : (data as any)?.error) || 'Failed to create PayPal order',
        };
      }

      return {
        success: true,
        orderId: data.orderId,
        approvalUrl: data.approvalUrl,
      };
    } catch (error: any) {
      console.error('Error creating PayPal order:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  }

  /**
   * Check the status of a PayPal payment
   */
  static async checkPaymentStatus(
    orderId: string
  ): Promise<PayPalStatusResponse> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return {
          success: false,
          error: 'User not authenticated',
        };
      }

      // Call the edge function to check payment status
      const { data, error } = await supabase.functions.invoke('paypal-check-status', {
        body: {
          orderId,
        },
      });

      if (error) {
        console.error('PayPal status check error:', error);
        return {
          success: false,
          error: error.message || 'Failed to check payment status',
        };
      }

      if (!data || !data.success) {
        return {
          success: false,
          error: data?.error || 'Failed to check payment status',
        };
      }

      return {
        success: true,
        completed: data.completed || false,
        cancelled: data.cancelled || false,
        orderId: data.orderId,
        payerId: data.payerId || undefined,
        payerEmail: data.payerEmail || undefined,
      };
    } catch (error: any) {
      console.error('Error checking PayPal status:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  }

  /**
   * Execute a PayPal payment after approval
   */
  static async executePayment(
    orderId: string,
    payerId: string
  ): Promise<PayPalOrderResponse> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return {
          success: false,
          error: 'User not authenticated',
        };
      }

      // Call the edge function to execute payment
      const { data, error } = await supabase.functions.invoke('paypal-execute-order', {
        body: {
          orderId,
          payerId,
        },
      });

      if (error) {
        console.error('PayPal execute error:', error);
        return {
          success: false,
          error: error.message || 'Failed to execute payment',
        };
      }

      if (!data || !data.success) {
        return {
          success: false,
          error: data?.error || 'Failed to execute payment',
        };
      }

      return {
        success: true,
        orderId: data.orderId,
        payerId: data.payerId || undefined,
        payerEmail: data.payerEmail || undefined,
      };
    } catch (error: any) {
      console.error('Error executing PayPal payment:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred',
      };
    }
  }
}

